import { useState, useEffect, useCallback } from 'react'
import { AuthUser } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import authService, { AuthCredentials, SignUpCredentials, AuthError } from '../services/authService'
import { sessionManager } from '../utils/sessionManager'

export interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: AuthError | null
  tokens: {
    accessToken: string
    idToken: string
    refreshToken?: string
  } | null
  userGroups: string[]
}

export interface AuthActions {
  signIn: (credentials: AuthCredentials) => Promise<void>
  signUp: (credentials: SignUpCredentials) => Promise<{ userId?: string; nextStep: unknown }>
  confirmSignUp: (username: string, code: string) => Promise<void>
  resendSignUpCode: (username: string) => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (username: string) => Promise<void>
  confirmResetPassword: (username: string, code: string, newPassword: string) => Promise<void>
  clearError: () => void
  refreshSession: () => Promise<void>
  hasAnyGroup: (groups: string[]) => boolean
}

export function useAuth(): AuthState & AuthActions {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    tokens: null,
    userGroups: []
  })

  // Initialize auth state
  const initializeAuth = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      
      // Check if we're in local development mode
      if (import.meta.env.VITE_LOCAL_DEV_MODE === 'true') {
        // Bypass authentication in local development
        setAuthState(prev => ({
          ...prev,
          user: {
            userId: 'local-dev-user',
            username: 'local-admin'
          } as AuthUser,
          isAuthenticated: true,
          tokens: {
            accessToken: 'local-dev-token',
            idToken: 'local-dev-id-token',
            refreshToken: 'local-dev-refresh-token'
          },
          userGroups: ['superAdmin'],
          isLoading: false
        }))
        return
      }
      
      const session = await authService.getCurrentSession()
      const userGroups = await authService.getUserGroups()
      
      setAuthState(prev => ({
        ...prev,
        user: session.user,
        isAuthenticated: session.isAuthenticated,
        tokens: session.tokens,
        userGroups,
        isLoading: false
      }))
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        tokens: null,
        userGroups: [],
        isLoading: false,
        error: error as AuthError
      }))
    }
  }, [])

  // Sign in
  const signIn = useCallback(async (credentials: AuthCredentials) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      const user = await authService.signIn(credentials)
      const session = await authService.getCurrentSession()
      const userGroups = await authService.getUserGroups()
      
      setAuthState(prev => ({
        ...prev,
        user,
        isAuthenticated: true,
        tokens: session.tokens,
        userGroups,
        isLoading: false
      }))

      // Start session monitoring after successful sign in
      sessionManager.startSessionMonitoring()
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as AuthError
      }))
      throw error
    }
  }, [])

  // Sign up
  const signUp = useCallback(async (credentials: SignUpCredentials) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      const result = await authService.signUp(credentials)
      
      setAuthState(prev => ({ ...prev, isLoading: false }))
      return result
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as AuthError
      }))
      throw error
    }
  }, [])

  // Confirm sign up
  const confirmSignUp = useCallback(async (username: string, code: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      await authService.confirmSignUp(username, code)
      
      setAuthState(prev => ({ ...prev, isLoading: false }))
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as AuthError
      }))
      throw error
    }
  }, [])

  // Resend sign up code
  const resendSignUpCode = useCallback(async (username: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      await authService.resendSignUpCode(username)
      
      setAuthState(prev => ({ ...prev, isLoading: false }))
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as AuthError
      }))
      throw error
    }
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      await authService.signOut()
      
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        tokens: null,
        userGroups: []
      })

      // Stop session monitoring after sign out
      sessionManager.stopSessionMonitoring()

      // Redirect to Cognito logout to clear the hosted UI session
      const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN
      const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID
      const logoutUrl = import.meta.env.VITE_LOGOUT_URL

      if (cognitoDomain && clientId && logoutUrl) {
        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUrl)}`
      }
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as AuthError
      }))
      throw error
    }
  }, [])

  // Reset password
  const resetPassword = useCallback(async (username: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      await authService.resetPassword(username)
      
      setAuthState(prev => ({ ...prev, isLoading: false }))
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as AuthError
      }))
      throw error
    }
  }, [])

  // Confirm reset password
  const confirmResetPassword = useCallback(async (
    username: string, 
    code: string, 
    newPassword: string
  ) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
      await authService.confirmResetPassword(username, code, newPassword)
      
      setAuthState(prev => ({ ...prev, isLoading: false }))
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as AuthError
      }))
      throw error
    }
  }, [])

  // Clear error
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }))
  }, [])

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      const session = await authService.getCurrentSession()
      const userGroups = await authService.getUserGroups()
      
      setAuthState(prev => ({
        ...prev,
        user: session.user,
        isAuthenticated: session.isAuthenticated,
        tokens: session.tokens,
        userGroups
      }))
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        tokens: null,
        userGroups: [],
        error: error as AuthError
      }))
    }
  }, [])

  // Check if user has any of the specified groups
  const hasAnyGroup = useCallback((groups: string[]): boolean => {
    return groups.some(group => authState.userGroups.includes(group))
  }, [authState.userGroups])

  // Listen to auth events
  useEffect(() => {
    const hubListener = (data: { payload: { event: string } }) => {
      const { event } = data.payload
      
      switch (event) {
        case 'signedIn':
          refreshSession()
          sessionManager.startSessionMonitoring()
          break
        case 'signedOut':
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            tokens: null,
            userGroups: []
          })
          sessionManager.stopSessionMonitoring()
          break
        case 'tokenRefresh':
          refreshSession()
          break
        case 'tokenRefresh_failure':
          setAuthState(prev => ({
            ...prev,
            error: {
              code: 'TokenRefreshFailure',
              message: 'Session expired. Please sign in again.'
            }
          }))
          break
        default:
          break
      }
    }

    // Subscribe to auth events
    const unsubscribe = Hub.listen('auth', hubListener)

    // Initialize auth state on mount
    initializeAuth()

    return () => {
      unsubscribe()
    }
  }, [initializeAuth, refreshSession])

  return {
    ...authState,
    signIn,
    signUp,
    confirmSignUp,
    resendSignUpCode,
    signOut,
    resetPassword,
    confirmResetPassword,
    clearError,
    refreshSession,
    hasAnyGroup
  }
}